import React, { useState, useEffect } from 'react';

const API = 'https://calendar.nareea.co.uk';

// Confirmation modal
function ConfirmModal({ title, message, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 400, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#3c4043', marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 14, color: '#5f6368', marginBottom: 24, lineHeight: 1.5 }}>{message}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '10px 20px', border: '1px solid #dadce0', borderRadius: 8, background: '#fff', color: '#3c4043', cursor: 'pointer', fontSize: 14 }}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{ padding: '10px 20px', border: 'none', borderRadius: 8, background: '#ea4335', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

const ArchivariusPanel = () => {
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priorities, setPriorities] = useState({});
  const [tab, setTab] = useState('pending');
  const [saved, setSaved] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { key, title }

  const loadRegistry = () => {
    fetch(`${API}/api/registry`)
      .then(r => r.json())
      .then(data => {
        setRegistry(data);
        setLoading(false);
        const init = {};
        (data.pending || []).forEach(item => { init[item.key] = item.suggested_priority; });
        Object.entries(data.events || {}).forEach(([key, val]) => { init[key] = val.priority; });
        setPriorities(init);
      })
      .catch(console.error);
  };

  useEffect(() => { loadRegistry(); }, []);

  const handleConfirm = async (key) => {
    const priority = priorities[key];
    const res = await fetch(`${API}/api/registry/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, priority: parseInt(priority) }),
    });
    const data = await res.json();
    if (data.success) {
      setSaved(prev => ({ ...prev, [key]: true }));
      setTimeout(() => { setSaved(prev => ({ ...prev, [key]: false })); loadRegistry(); }, 1500);
    }
  };

  const handleUpdate = async (key) => {
    const priority = priorities[key];
    const res = await fetch(`${API}/api/registry/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, priority: parseInt(priority) }),
    });
    const data = await res.json();
    if (data.success) {
      setSaved(prev => ({ ...prev, [key]: true }));
      setTimeout(() => { setSaved(prev => ({ ...prev, [key]: false })); loadRegistry(); }, 1500);
    }
  };

  const handleDelete = async (key) => {
    const res = await fetch(`${API}/api/registry/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    });
    const data = await res.json();
    if (data.success) {
      setDeleteConfirm(null);
      loadRegistry();
    }
  };

  const setPriority = (key, val) => {
    const v = Math.min(10, Math.max(1, parseInt(val) || 1));
    setPriorities(prev => ({ ...prev, [key]: v }));
  };

  const getPriorityColor = (p) => {
    if (p >= 9) return '#ea4335';
    if (p >= 7) return '#fa7b17';
    if (p >= 5) return '#fbbc04';
    return '#34a853';
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading registry...</div>;

  const pending = registry?.pending || [];
  const events = Object.entries(registry?.events || {});

  return (
    <div style={{ maxWidth: 1100, margin: '20px auto', padding: '0 16px', fontFamily: 'Google Sans, Arial, sans-serif' }}>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <ConfirmModal
          title="Delete event from registry?"
          message={`Are you sure you want to delete "${deleteConfirm.title}" from the registry? This action cannot be undone.`}
          onConfirm={() => handleDelete(deleteConfirm.key)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, color: '#1a73e8', fontSize: 20 }}>AI Archivarius</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#5f6368' }}>
              Event registry — {events.length} known · {pending.length} pending confirmation
            </p>
          </div>
          <button onClick={loadRegistry} style={{ background: '#e8f0fe', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#1a73e8', cursor: 'pointer', fontSize: 13 }}>
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid #dadce0', paddingBottom: 12 }}>
          <button onClick={() => setTab('pending')} style={{ background: tab === 'pending' ? '#fce8e6' : 'none', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, color: tab === 'pending' ? '#ea4335' : '#5f6368', fontWeight: tab === 'pending' ? 600 : 400 }}>
            Pending ({pending.length})
          </button>
          <button onClick={() => setTab('known')} style={{ background: tab === 'known' ? '#e8f0fe' : 'none', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, color: tab === 'known' ? '#1a73e8' : '#5f6368', fontWeight: tab === 'known' ? 600 : 400 }}>
            Known ({events.length})
          </button>
        </div>

        {/* Pending tab */}
        {tab === 'pending' && (
          <div>
            {pending.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#34a853', background: '#e6f4ea', borderRadius: 8 }}>
                All events confirmed — no pending items
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {pending.map((item, i) => {
                const currentPriority = priorities[item.key] ?? item.suggested_priority;
                return (
                  <div key={i} style={{ border: `2px solid ${getPriorityColor(currentPriority)}`, borderRadius: 10, padding: 14, background: '#fef7f7', transition: 'border-color 0.2s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div style={{ flex: 1, marginRight: 8 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#3c4043' }}>{item.title}</div>
                        <div style={{ fontSize: 11, color: '#5f6368', marginTop: 2 }}>
                          {item.type} · {new Date(item.first_seen).toLocaleDateString()}
                        </div>
                      </div>
                      <div style={{ background: getPriorityColor(item.suggested_priority), color: '#fff', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        AI: {item.suggested_priority}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: getPriorityColor(currentPriority), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0, transition: 'background 0.2s' }}>
                        {currentPriority}
                      </div>
                      <input type="range" min="1" max="10" value={currentPriority} onChange={(e) => setPriority(item.key, e.target.value)} style={{ flex: 1, accentColor: getPriorityColor(currentPriority) }} />
                      <input type="number" min="1" max="10" value={currentPriority} onChange={(e) => setPriority(item.key, e.target.value)} style={{ width: 48, padding: '4px 6px', border: `1px solid ${getPriorityColor(currentPriority)}`, borderRadius: 6, fontSize: 13, textAlign: 'center', fontWeight: 600 }} />
                    </div>

                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => handleConfirm(item.key)}
                        style={{ flex: 1, background: saved[item.key] ? '#34a853' : '#1a73e8', color: '#fff', border: 'none', borderRadius: 6, padding: '8px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        {saved[item.key] ? 'Saved!' : 'Confirm'}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ key: item.key, title: item.title })}
                        style={{ background: '#fce8e6', color: '#ea4335', border: '1px solid #ea4335', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Known events tab */}
        {tab === 'known' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
            {events.map(([key, val], i) => {
              const currentPriority = priorities[key] ?? val.priority;
              return (
                <div key={i} style={{ border: '1px solid #dadce0', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: getPriorityColor(currentPriority), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0, transition: 'background 0.2s' }}>
                      {currentPriority}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: '#3c4043', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{key}</div>
                      <div style={{ fontSize: 10, color: '#5f6368' }}>{val.type} · {val.count || 0}x used</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="range" min="1" max="10" value={currentPriority} onChange={(e) => setPriority(key, e.target.value)} style={{ flex: 1, accentColor: getPriorityColor(currentPriority) }} />
                    <input type="number" min="1" max="10" value={currentPriority} onChange={(e) => setPriority(key, e.target.value)} style={{ width: 44, padding: '3px 4px', border: `1px solid ${getPriorityColor(currentPriority)}`, borderRadius: 4, fontSize: 12, textAlign: 'center', fontWeight: 600 }} />
                  </div>

                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => handleUpdate(key)}
                      style={{ flex: 1, background: saved[key] ? '#34a853' : '#f1f3f4', color: saved[key] ? '#fff' : '#3c4043', border: 'none', borderRadius: 6, padding: '6px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                    >
                      {saved[key] ? 'Saved!' : 'Update'}
                    </button>
                    <button
                      onClick={() => setDeleteConfirm({ key, title: key })}
                      style={{ background: '#fce8e6', color: '#ea4335', border: '1px solid #ea4335', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchivariusPanel;

